/**
 * A wall length as somebody types it on the review screen (plan 4 §5) — in
 * millimetres, or the reason it is not one.
 *
 * Sits beside `parseMetres` (`lib/draft.ts`), which reads a field labelled
 * „m“. This one reads a field without a unit, so the unit has to come out of
 * the text itself, in this order:
 *
 * 1. **A unit wins.** „512 cm“, „5,12 m“, „5120 mm“ say what they are.
 * 2. **A whole number from 100 up is millimetres.** „5120“ is how a drawing
 *    writes it.
 * 3. **A decimal separator means metres.** „5,12“ = „5.12“ = 5.120 mm.
 *    „5.120“ reads the same either way: as metres it is 5,120 m, as a
 *    thousands-grouped millimetre figure it is 5.120 mm — the two readings
 *    never disagree, which is why the German grouping dot needs no special
 *    case here (the ambiguity `core/units.py` must resolve, MK-02, does not
 *    arise for one wall).
 * 4. **A whole number under 100 is metres.** Nobody builds a wall of 45 mm,
 *    and „45“ beside a facade is 45 m.
 *
 * Bounds are the same band the server holds (plan 4 §4, W3): 100 to 100.000
 * millimetres. A typed value is never rounded into the band.
 */

export const MIN_LENGTH_MM = 100;
export const MAX_LENGTH_MM = 100_000;

export type LengthRefusal = "empty" | "unreadable" | "too_short" | "too_long";

export type ParsedLength =
  | { ok: true; mm: number }
  | { ok: false; reason: LengthRefusal };

const UNIT_FACTOR: Record<string, number> = { mm: 1, cm: 10, m: 1000 };

/** Number, optional space, optional unit. Nothing before, nothing after. */
const SHAPE = /^(\d+(?:[.,]\d+)*)\s*(mm|cm|m)?$/i;

/** „12.500“, „1.234.567“ — digits grouped in threes by dots, nothing else. */
const GROUPED = /^\d{1,3}(?:\.\d{3})+$/;

export function parseLengthMm(text: string): ParsedLength {
  const trimmed = text.trim().replace(/\u00a0/g, " ");
  if (trimmed === "") return { ok: false, reason: "empty" };

  const match = SHAPE.exec(trimmed);
  if (match === null) return { ok: false, reason: "unreadable" };
  const digits = match[1]!;
  const unit = match[2]?.toLowerCase() ?? null;

  const separators = (digits.match(/[.,]/g) ?? []).length;
  // A dot before exactly three digits is the German thousands dot wherever a
  // decimal would make no sense: „1.500 mm“ is not one and a half
  // millimetres. Before metres it stays a decimal point — „5.120 m“ — unless
  // there are several, which only a grouped figure has.
  const grouped = GROUPED.test(digits) && (unit !== "m" || separators > 1);
  if (grouped) {
    const whole = Number(digits.replace(/\./g, ""));
    return bounded(whole * (unit === null ? 1 : UNIT_FACTOR[unit]!));
  }
  // „1,2,3“ or „1.2,5“ is not a number anybody meant.
  if (separators > 1) return { ok: false, reason: "unreadable" };

  const value = Number(digits.replace(",", "."));
  if (!Number.isFinite(value)) return { ok: false, reason: "unreadable" };

  let raw: number;
  if (unit !== null) raw = value * UNIT_FACTOR[unit]!;
  else if (separators === 1) raw = value * 1000;
  else raw = value >= MIN_LENGTH_MM ? value : value * 1000;
  return bounded(raw);
}

/** Checked before rounding: 99,9 mm is not rounded up into the band. */
function bounded(raw: number): ParsedLength {
  if (raw < MIN_LENGTH_MM) return { ok: false, reason: "too_short" };
  if (raw > MAX_LENGTH_MM) return { ok: false, reason: "too_long" };
  return { ok: true, mm: Math.round(raw) };
}

/** The sentence under a field for each refusal (plan 4 §5, Mikrocopy). */
export const LENGTH_REFUSALS: Record<Exclude<LengthRefusal, "empty">, string> = {
  unreadable: "Kein Maß erkannt — z. B. 5,12 oder 5120.",
  too_short: "Unter 100 mm — so kurz ist keine Wand.",
  too_long: "Über 100 m — prüf die Zahl.",
};

/**
 * From this length on — itself included — a typed wall length asks once
 * before it counts (UX-Gutachten
 * 23.10.2026, Nr. 7): „60“ meant as „6,0“ is read as 60 m by rule 4 above, and
 * nothing on the board said so — the drawing does not move with a figure, so
 * the geometry cannot show the slip (E-13) the way it shows a wrong reading.
 *
 * 30 m, because a Hausprojekt is a facade: a detached or semi-detached house
 * runs well under it on every side, and the Vorgänge on file do. **30 m
 * itself asks too** (review of d8516611, M-7, decided by the controller):
 * „30“ is „3,0“ with the comma forgotten at a three-metre wall, exactly the
 * slip the question is for, and 30,00 m is as unlikely on a house as 30,01 m.
 * **A question,
 * not a bound** — a terrace row, a hall, a long garden wall exist, and the
 * server's band (100 mm … 100 m, above) stays where it is. The figure is the
 * person's to keep (A-014); the field only asks whether it is meant.
 */
export const WALL_DOUBT_FROM_MM = 30_000;

/**
 * The figure to offer instead of an implausible one — one place lower, the
 * slip both ways in produce („60“ for „6,0“, „60000“ for „6000“) — or `null`
 * where nothing needs asking. Whole millimetres, like every typed length.
 */
export function wallLengthDoubt(mm: number): number | null {
  return mm >= WALL_DOUBT_FROM_MM ? Math.round(mm / 10) : null;
}
